import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useGetCurrentUser } from "@src/appV2/redesign/MyAccount/api/useGetCurrentUser";
import constate from "constate";
import { useCallback, useRef } from "react";

import { KNOCK_TOKEN_REFRESH_THROTTLE_MS } from "./constants";

interface UseKnockTokenOptions {
  userId: string | undefined;
}

function useKnockToken({ userId }: UseKnockTokenOptions) {
  const refreshPromiseRef = useRef<Promise<string | undefined>>();
  const lastRefreshAttemptAtRef = useRef<number>(0);

  const { data, refetch: refetchUser, isLoading } = useGetCurrentUser();
  const knockToken = data?.user?.knockToken;

  const handleKnockTokenExpiring = useCallback(async () => {
    if (refreshPromiseRef.current) {
      logEvent(APP_V2_APP_EVENTS.KNOCK_TOKEN_REFRESH_REQUESTED, {
        userId,
        hasRecentAttempt: false,
        hasPerformedRefresh: false,
      });
      return await refreshPromiseRef.current;
    }

    const hasRecentAttempt =
      Date.now() - lastRefreshAttemptAtRef.current < KNOCK_TOKEN_REFRESH_THROTTLE_MS;

    if (hasRecentAttempt) {
      logEvent(APP_V2_APP_EVENTS.KNOCK_TOKEN_REFRESH_REQUESTED, {
        userId,
        hasRecentAttempt: true,
        hasPerformedRefresh: false,
      });
      return knockToken;
    }

    lastRefreshAttemptAtRef.current = Date.now();

    const refreshPromise = (async () => {
      try {
        const { data: refreshedData } = await refetchUser();
        logEvent(APP_V2_APP_EVENTS.KNOCK_TOKEN_REFRESH_REQUESTED, {
          userId,
          hasRecentAttempt: false,
          hasPerformedRefresh: true,
        });
        return refreshedData?.user?.knockToken;
      } catch (error) {
        logError(APP_V2_APP_EVENTS.KNOCK_TOKEN_REFRESH_FAILED, { error });
        return undefined;
      } finally {
        refreshPromiseRef.current = undefined;
      }
    })();

    refreshPromiseRef.current = refreshPromise;

    return await refreshPromise;
  }, [refetchUser, knockToken, userId]);

  return {
    knockToken,
    isLoading,
    handleKnockTokenExpiring,
  };
}

export const [KnockTokenProvider, useKnockTokenContext] = constate(useKnockToken);
